/**
 * QUIPWA — conflict-free merge of `_state.db`
 *
 * `mergeReplicatedStates(a, b)` is a pure function with three properties the
 * tests check on randomly generated histories:
 *
 *   commutative   merge(a, b) ≡ merge(b, a)
 *   associative   merge(merge(a, b), c) ≡ merge(a, merge(b, c))
 *   idempotent    merge(m, m) ≡ m   for any merged state m
 *
 * Together they mean every device converges to the same state whatever the
 * order of syncs or how often they repeat — so there is nothing to ask the user.
 *
 * Rules (docs/modulos/replicacion.md):
 *
 * | Data          | Rule                                                              |
 * |---------------|-------------------------------------------------------------------|
 * | review_log    | grow-only set, keyed by (card, deck, reviewed_at, session)        |
 * | review_state  | newest review wins (`last_review`), then HLC `updated_at`         |
 * | other rows    | last-writer-wins by HLC `updated_at`                              |
 * | row deletion  | tombstone; removes writes stamped at or before it (re-adding works)|
 * | deck / tag    | deletion is final: ids are UUIDs and never come back              |
 * | deck deleted  | its review state, log and tag links go with it                    |
 * | socratic item | last-writer-wins; deletion is final (UUIDs)                       |
 * | socratic attempt / event | grow-only sets keyed by id                             |
 *
 * Two implementation rules keep the merge associative:
 *  1. Tombstones are applied *before* choosing a winner (filter, then max), so
 *     dropping a row early in one merge order can never change the winner in
 *     another order.
 *  2. Nothing is derived from "is the parent row present", only from tombstones,
 *     which only ever grow.
 *
 * Tag *names* are deliberately not deduplicated here: two devices may create
 * "ciencia" offline. Both rows replicate; reads collapse them by name (see
 * StateDBService). Deduplicating inside the merge would make it order-dependent.
 */

import { compareHlc, formatHlc, maxHlc } from './hlc.ts'
import {
  DECK_INSTANCES, DECK_TAGS, REVIEW_LOGS, REVIEW_STATES, SESSIONS, SESSION_DECKS, SETTINGS, TAGS, TOMBSTONES,
  SOCRATIC_ATTEMPTS, SOCRATIC_EVENTS, SOCRATIC_ITEMS,
  rowSignature,
  type DeckInstanceRow, type DeckTagRow, type ReplicatedState, type ReviewStateRow, type SessionDeckRow,
  type SessionRow, type SettingRow, type SocraticAttemptRow, type SocraticEventRow, type SocraticItemRow,
  type TableSpec, type TagRow, type Tombstone, type TombstoneEntity,
} from './state-tables.ts'

// ─── comparators: > 0 means `a` wins ─────────────────────────────────────────

type Comparator<Row> = (a: Row, b: Row) => number

/** Deterministic last resort, so ties resolve identically on every device. */
function bySignature<Row>(table: TableSpec<Row>): Comparator<Row> {
  return (a, b) => {
    const sa = rowSignature(table, a)
    const sb = rowSignature(table, b)
    return sa > sb ? 1 : sa < sb ? -1 : 0
  }
}

function chain<Row>(...comparators: Comparator<Row>[]): Comparator<Row> {
  return (a, b) => {
    for (const cmp of comparators) {
      const r = cmp(a, b)
      if (r !== 0) return r
    }
    return 0
  }
}

const byUpdatedAt = <Row extends { updated_at: string }>(a: Row, b: Row) => compareHlc(a.updated_at, b.updated_at)

const compareSettings = chain<SettingRow>(byUpdatedAt, bySignature(SETTINGS))
const compareDeckInstances = chain<DeckInstanceRow>(byUpdatedAt, bySignature(DECK_INSTANCES))
const compareTags = chain<TagRow>(byUpdatedAt, bySignature(TAGS))
const compareDeckTags = chain<DeckTagRow>(byUpdatedAt, bySignature(DECK_TAGS))
const compareSessionDecks = chain<SessionDeckRow>(byUpdatedAt, bySignature(SESSION_DECKS))
const compareSocraticItems = chain<SocraticItemRow>(byUpdatedAt, bySignature(SOCRATIC_ITEMS))
const compareSessions = chain<SessionRow>(
  byUpdatedAt,
  // Rows from app versions that don't stamp: a finished session beats an
  // open one, more reviewed cards beat fewer.
  (a, b) => Number(a.ended_at !== null) - Number(b.ended_at !== null),
  (a, b) => a.cards_reviewed - b.cards_reviewed,
  bySignature(SESSIONS)
)
/**
 * The effective version of a card's review state: its HLC stamp, or the time
 * of its last review if that is later. The second term matters for rows
 * written by older app versions, which update `last_review` but leave
 * `updated_at` untouched (or empty) — their newer reviews must still win.
 *
 * The same value is used to pick the winner *and* to test it against
 * tombstones. Two different orderings would make the merge order-dependent:
 * a row discarded early for being "older" could be the only one a later
 * deletion leaves standing.
 */
export function reviewStateVersion(row: ReviewStateRow): string {
  if (row.last_review === null) return row.updated_at
  return maxHlc(row.updated_at, formatHlc({ wall: row.last_review * 1000, counter: 0, node: '0' }))
}

const compareReviewStates = chain<ReviewStateRow>(
  (a, b) => compareHlc(reviewStateVersion(a), reviewStateVersion(b)),
  (a, b) => a.reps - b.reps,
  bySignature(REVIEW_STATES)
)

// ─── tombstones ──────────────────────────────────────────────────────────────

class TombstoneSet {
  private readonly byId = new Map<string, Tombstone>()

  constructor(...sources: Tombstone[][]) {
    for (const t of sources.flat()) {
      const id = `${t.entity}:${t.key}`
      const current = this.byId.get(id)
      if (!current || compareHlc(t.deleted_at, current.deleted_at) > 0) this.byId.set(id, t)
    }
  }

  has(entity: TombstoneEntity, key: string): boolean {
    return this.byId.has(`${entity}:${key}`)
  }

  /** Was a row with this stamp deleted afterwards (or at the same instant)? */
  covers(entity: TombstoneEntity, key: string, updatedAt: string): boolean {
    const t = this.byId.get(`${entity}:${key}`)
    return !!t && compareHlc(t.deleted_at, updatedAt) >= 0
  }

  sorted(): Tombstone[] {
    return [...this.byId.values()].sort((x, y) => cmpString(TOMBSTONES.key(x), TOMBSTONES.key(y)))
  }
}

// ─── generic building blocks ─────────────────────────────────────────────────

/** Filter, then pick the winner per key. */
function lww<Row>(
  table: TableSpec<Row>,
  cmp: Comparator<Row>,
  rows: Row[],
  alive: (row: Row, key: string) => boolean
): Map<string, Row> {
  const out = new Map<string, Row>()
  for (const row of rows) {
    const key = table.key(row)
    if (!alive(row, key)) continue
    const current = out.get(key)
    if (!current || cmp(row, current) > 0) out.set(key, row)
  }
  return out
}

function cmpString(a: string, b: string): number {
  return a < b ? -1 : a > b ? 1 : 0
}

function sorted<Row>(table: TableSpec<Row>, rows: Map<string, Row>): Row[] {
  return [...rows.values()].sort((x, y) => cmpString(table.key(x), table.key(y)))
}

/** Grow-only set: every key ever written stays. The same row twice resolves deterministically. */
function union<Row>(table: TableSpec<Row>, rows: Row[]): Map<string, Row> {
  const out = new Map<string, Row>()
  for (const row of rows) {
    const key = table.key(row)
    const current = out.get(key)
    if (!current || rowSignature(table, row) > rowSignature(table, current)) out.set(key, row)
  }
  return out
}

/**
 * A deck's Drive binding (file id + Drive modification time) is merged as its
 * own register: the most recently modified binding seen on any replica wins,
 * independently of which row won the other fields. Otherwise a device that
 * renamed the deck before seeing another device's upload would erase the
 * binding, and the deck would be uploaded again as a duplicate file.
 */
function withDriveBinding(winner: DeckInstanceRow, candidates: DeckInstanceRow[]): DeckInstanceRow {
  const bound = candidates
    .filter((r) => r.drive_file_id)
    .sort((x, y) => y.drive_modified_at - x.drive_modified_at || cmpString(x.drive_file_id, y.drive_file_id))[0]
  return bound ? { ...winner, drive_file_id: bound.drive_file_id, drive_modified_at: bound.drive_modified_at } : winner
}

// ─── merge ───────────────────────────────────────────────────────────────────

export function mergeReplicatedStates(a: ReplicatedState, b: ReplicatedState): ReplicatedState {
  const tomb = new TombstoneSet(a.tombstones, b.tombstones)
  const deckDeleted = (deckId: string) => tomb.has('deck_instance', deckId)
  const tagDeleted = (tagId: string) => tomb.has('tag', tagId)

  const settings = lww(SETTINGS, compareSettings, [...a.settings, ...b.settings], () => true)

  // Decks: deletion is final.
  const deckRows = [...a.deckInstances, ...b.deckInstances].filter((r) => !deckDeleted(r.id))
  const decks = lww(DECK_INSTANCES, compareDeckInstances, deckRows, () => true)
  for (const [id, winner] of decks) {
    decks.set(id, withDriveBinding(winner, deckRows.filter((r) => r.id === id)))
  }

  const tags = lww(TAGS, compareTags, [...a.tags, ...b.tags], (r) => !tagDeleted(r.id))

  const deckTags = lww(DECK_TAGS, compareDeckTags, [...a.deckTags, ...b.deckTags], (r, key) =>
    !deckDeleted(r.deck_instance_id) && !tagDeleted(r.tag_id) && !tomb.covers('deck_tag', key, r.updated_at)
  )

  const sessions = lww(SESSIONS, compareSessions, [...a.sessions, ...b.sessions], (r, key) =>
    !tomb.covers('session', key, r.updated_at)
  )

  const sessionDecks = lww(SESSION_DECKS, compareSessionDecks, [...a.sessionDecks, ...b.sessionDecks], () => true)

  const reviewStates = lww(REVIEW_STATES, compareReviewStates, [...a.reviewStates, ...b.reviewStates], (r, key) =>
    !deckDeleted(r.deck_instance_id) && !tomb.covers('review_state', key, reviewStateVersion(r))
  )

  const reviewLogs = new Map<string, ReplicatedState['reviewLogs'][number]>()
  for (const log of [...a.reviewLogs, ...b.reviewLogs]) {
    if (deckDeleted(log.deck_instance_id)) continue
    const key = REVIEW_LOGS.key(log)
    const current = reviewLogs.get(key)
    // Same review recorded twice: identical in practice; pick deterministically anyway.
    if (!current || rowSignature(REVIEW_LOGS, log) > rowSignature(REVIEW_LOGS, current)) reviewLogs.set(key, log)
  }

  // Socratic exam. Attempts and events outlive a deleted item on purpose:
  // they are the study history, and fold() skips items it no longer has.
  const socraticItems = lww(SOCRATIC_ITEMS, compareSocraticItems, [...a.socraticItems, ...b.socraticItems], (r) =>
    !tomb.has('socratic_item', r.id)
  )
  const socraticAttempts = union(SOCRATIC_ATTEMPTS, [...a.socraticAttempts, ...b.socraticAttempts])
  const socraticEvents = union(SOCRATIC_EVENTS, [...a.socraticEvents, ...b.socraticEvents])

  return {
    settings: sorted(SETTINGS, settings),
    deckInstances: sorted(DECK_INSTANCES, decks),
    tags: sorted(TAGS, tags),
    deckTags: sorted(DECK_TAGS, deckTags),
    sessions: sorted(SESSIONS, sessions),
    sessionDecks: sorted(SESSION_DECKS, sessionDecks),
    reviewStates: sorted(REVIEW_STATES, reviewStates),
    reviewLogs: sorted(REVIEW_LOGS, reviewLogs),
    socraticItems: sorted(SOCRATIC_ITEMS, socraticItems),
    socraticAttempts: sorted(SOCRATIC_ATTEMPTS, socraticAttempts),
    socraticEvents: sorted(SOCRATIC_EVENTS, socraticEvents),
    tombstones: tomb.sorted(),
  }
}

// ─── diffing: what changes locally, does the remote need an update ──────────

export interface TableChanges<Row> {
  upserts: Row[]
  deletes: Row[]
}

export interface ReplicationChanges {
  settings: TableChanges<SettingRow>
  deckInstances: TableChanges<DeckInstanceRow>
  tags: TableChanges<TagRow>
  deckTags: TableChanges<DeckTagRow>
  sessions: TableChanges<SessionRow>
  sessionDecks: TableChanges<SessionDeckRow>
  reviewStates: TableChanges<ReviewStateRow>
  reviewLogs: TableChanges<ReplicatedState['reviewLogs'][number]>
  socraticItems: TableChanges<SocraticItemRow>
  socraticAttempts: TableChanges<SocraticAttemptRow>
  socraticEvents: TableChanges<SocraticEventRow>
  tombstones: TableChanges<Tombstone>
}

function diffTable<Row>(table: TableSpec<Row>, from: Row[], to: Row[]): TableChanges<Row> {
  const before = new Map(from.map((r) => [table.key(r), rowSignature(table, r)]))
  const after = new Set(to.map((r) => table.key(r)))
  return {
    upserts: to.filter((r) => before.get(table.key(r)) !== rowSignature(table, r)),
    deletes: from.filter((r) => !after.has(table.key(r))),
  }
}

/** Row-level changes that turn `from` into `to`. */
export function diffReplicatedStates(from: ReplicatedState, to: ReplicatedState): ReplicationChanges {
  return {
    settings: diffTable(SETTINGS, from.settings, to.settings),
    deckInstances: diffTable(DECK_INSTANCES, from.deckInstances, to.deckInstances),
    tags: diffTable(TAGS, from.tags, to.tags),
    deckTags: diffTable(DECK_TAGS, from.deckTags, to.deckTags),
    sessions: diffTable(SESSIONS, from.sessions, to.sessions),
    sessionDecks: diffTable(SESSION_DECKS, from.sessionDecks, to.sessionDecks),
    reviewStates: diffTable(REVIEW_STATES, from.reviewStates, to.reviewStates),
    reviewLogs: diffTable(REVIEW_LOGS, from.reviewLogs, to.reviewLogs),
    socraticItems: diffTable(SOCRATIC_ITEMS, from.socraticItems, to.socraticItems),
    socraticAttempts: diffTable(SOCRATIC_ATTEMPTS, from.socraticAttempts, to.socraticAttempts),
    socraticEvents: diffTable(SOCRATIC_EVENTS, from.socraticEvents, to.socraticEvents),
    tombstones: diffTable(TOMBSTONES, from.tombstones, to.tombstones),
  }
}

export function countChanges(changes: ReplicationChanges): number {
  return Object.values(changes).reduce((n, t: TableChanges<unknown>) => n + t.upserts.length + t.deletes.length, 0)
}

export interface MergeOutcome {
  merged: ReplicatedState
  /** What has to be written to the local database. */
  localChanges: ReplicationChanges
  /** True when the remote copy lacks something the merge kept: it needs a push. */
  remoteNeedsUpdate: boolean
}

export function reconcile(local: ReplicatedState, remote: ReplicatedState): MergeOutcome {
  const merged = mergeReplicatedStates(local, remote)
  return {
    merged,
    localChanges: diffReplicatedStates(local, merged),
    remoteNeedsUpdate: countChanges(diffReplicatedStates(remote, merged)) > 0,
  }
}
